import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  CloseButton,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Spinner,
  Text,
  Tooltip,
  Image,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { useContext, useMemo, useState } from "react";
import {
  FaCalendar,
  FaInfo,
  FaToolbox,
  FaArrowUp,
  FaArrowDown,
  FaLink,
  FaDirections,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { usePoi } from "../../data/mazemap.api";
import { useBookingLocation } from "../../data/occupancy.api";
import { useFocusOnFeature } from "../../hooks/useFocusOnFeature";
import BackButton from "../BackButton";
import BottomSheetWrapper from "../BottomSheetWrapper";
import { AuthContext } from "../../context/auth.context";
import ImageCarousel from "./ImageCarousel";
import DirectionsButtonMazemap from "./DirectionsButtonMazemap";

const FIX_A_FAULT =
  "https://www.lancaster.ac.uk/facilities/helpdesk/report-a-fault/";

const environmentUrlRooms = () => {
  const prod = "https://www.lancaster.ac.uk/itpi/web/services/room";
  const staging = "https://www.lancaster.ac.uk/itpi/test/web/services/room";
  const localhost = "http://localhost:5173/services/room";

  if (window.location.href.includes("localhost")) {
    return `${localhost}`;
  } else if (window.location.href.includes("/itpi/test/lighthouse/")) {
    return `${staging}`;
  } else {
    return `${prod}`;
  }
};

type Props = {
  id: string;
};

const PoiSearchResult = ({ id }: Props) => {
  const { userDetails } = useContext(AuthContext);

  const isIlancasterStaff =
    userDetails?.["cognito:groups"]?.includes("iLancaster-staff");

  const { data, isLoading, isError } = usePoi(parseInt(id));
  const { lng, lat } = useFocusOnFeature(data, 19);
  const [viewImage, setViewImage] = useState(false);

  const { data: bookingData } = useBookingLocation(
    data?.properties?.identifier
  );

  const propertyType = data?.properties.names.slice(1).join(", ");

  console.log("data for point -> ", data);

  return (
    <BottomSheetWrapper>
      {isLoading ? (
        <Center p={8}>
          <Spinner />
        </Center>
      ) : isError ? (
        <Center p={8}>
          <Alert status="error">
            <AlertIcon />
            Could not find a POI with ID "{id}".
          </Alert>
        </Center>
      ) : data ? (
        <>
          {bookingData?.images && bookingData.images.length > 0 ? (
            <Box
              h={24}
              bg={`url(${bookingData.images[0].url})`}
              bgPosition="center"
              bgSize="cover"
            />
          ) : null}
          <Box p={5} width="100%">
            <BackButton />
            <Box mb={3}>
              <Heading size="md">{data.properties?.title}</Heading>
              {data.properties.names.length > 1 ? (
                <Text mt={1} opacity={0.7}>
                  ({propertyType})
                </Text>
              ) : null}
            </Box>
            {data.properties.description ? (
              <Text mb={3}>{data.properties.description}</Text>
            ) : null}
            {data.properties.infoUrl ? (
              <HStack mb={3}>
                <Icon color={"#006382"} as={FaLink}></Icon>
                <ChakraLink
                  isExternal
                  color={"#006382"}
                  textDecoration="underline"
                  fontStyle="italic"
                  href={data.properties.infoUrl}
                >
                  {data.properties.infoUrl}
                </ChakraLink>
              </HStack>
            ) : null}
            {viewImage ? (
              <ImageCarousel images={data.properties.images} />
            ) : null}

            <Flex flexWrap="wrap" gap={2} alignItems="center" pt={4}>
              {data.properties.identifier && (
                <DirectionsButtonMazemap
                  plannonID={data.properties.identifier}
                />
              )}

              {bookingData && bookingData.availableDesks > 0 ? (
                <Button
                  leftIcon={<FaCalendar />}
                  as={Link}
                  to={`/book/${bookingData.key}`}
                >
                  Book a space
                </Button>
              ) : null}
              <Button
                as={ChakraLink}
                isExternal
                href={FIX_A_FAULT}
                leftIcon={<FaToolbox />}
              >
                Report a fault
              </Button>
              {isIlancasterStaff ? (
                <Button
                  as={ChakraLink}
                  isExternal
                  href={`${environmentUrlRooms()}/${
                    data?.properties?.identifier
                  }`}
                  leftIcon={<FaToolbox />}
                >
                  Room Information
                </Button>
              ) : null}
              {data.properties.images.length > 0 ? (
                <Button
                  leftIcon={viewImage ? <FaArrowDown /> : <FaArrowUp />}
                  onClick={() => setViewImage(!viewImage)}
                >
                  {viewImage ? `Hide Images` : `View Images`}
                </Button>
              ) : null}
              <Popover placement="top">
                <PopoverTrigger>
                  <IconButton
                    aria-label="More information"
                    icon={<Icon as={FaInfo} opacity={0.6} />}
                  />
                </PopoverTrigger>
                <Portal>
                  <PopoverContent>
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverHeader>POI Details</PopoverHeader>
                    <PopoverBody fontSize=".8em">
                      <Text>PlannonID: {data.properties.identifier}</Text>
                      <Text>
                        Lng/Lat: {lng}/{lat}
                      </Text>
                    </PopoverBody>
                  </PopoverContent>
                </Portal>
              </Popover>
            </Flex>
          </Box>
        </>
      ) : null}
    </BottomSheetWrapper>
  );
};

export default PoiSearchResult;
